import React, { useState } from "react";
import { Badge, Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { WS_TYPES } from "../wsProtocol";

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// floating files hub for students: download what the teacher shared and
// send files back to the teacher (works for guests via the board token)
const FilesChip = ({ sharedFiles, uploadBoardFile, send }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const inputRef = React.useRef(null);
  const [seenCount, setSeenCount] = useState(0);
  const unseen = Math.max(0, sharedFiles.length - seenCount);

  const sendFile = (file) => {
    setIsSending(true);
    uploadBoardFile(file)
      .then((data) => {
        send({ t: WS_TYPES.FILE_SUBMIT, name: data.name, url: data.url, size: data.size });
        setSentCount((n) => n + 1);
      })
      .catch(() => { })
      .finally(() => setIsSending(false));
  };

  return (
    <>
      <Badge badgeContent={unseen} color="error" sx={{ position: "fixed", bottom: 92, insetInlineEnd: 28, zIndex: 30 }}>
        <Button
          variant="contained"
          color="inherit"
          onClick={() => { setOpen((v) => !v); setSeenCount(sharedFiles.length); }}
          sx={{ borderRadius: "100vh", fontWeight: 800, background: "#fff", boxShadow: "0 4px 18px rgba(0,0,0,0.25)" }}
        >
          📎 {t("wb_files")}
        </Button>
      </Badge>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 140,
            insetInlineEnd: 24,
            zIndex: 31,
            width: "min(340px, 92vw)",
            maxHeight: "50vh",
            overflowY: "auto",
            borderRadius: "16px",
            padding: "14px 16px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
            <Typography fontWeight={800} sx={{ flex: 1 }}>📎 {t("wb_files")}</Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>✕</IconButton>
          </Box>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            style={{ display: "none" }}
            onChange={(event) => {
              const file = event.target.files[0];
              event.target.value = "";
              if (file) sendFile(file);
            }}
          />
          <Button
            fullWidth
            variant="outlined"
            disabled={isSending || sentCount >= 10}
            onClick={() => inputRef.current && inputRef.current.click()}
            sx={{ borderRadius: "12px", fontWeight: 700, marginBottom: 1.5 }}
          >
            {isSending ? "..." : `📤 ${t("wb_send_file_to_teacher")}`}
          </Button>
          {sentCount > 0 && (
            <Typography fontSize="0.75rem" color="success.main" mb={1}>✓ {t("wb_file_sent", { count: sentCount })}</Typography>
          )}

          {sharedFiles.length === 0 ? (
            <Typography fontSize="0.85rem" color="text.secondary">{t("wb_no_files_yet")}</Typography>
          ) : (
            [...sharedFiles].reverse().map((file) => (
              <Box key={file.id} sx={{ display: "flex", alignItems: "center", gap: 1, background: "#F8F8FA", borderRadius: "10px", padding: "8px 10px", marginBottom: "6px" }}>
                <Typography fontSize="0.85rem" fontWeight={600} sx={{ flex: 1, wordBreak: "break-word" }}>
                  {file.name} <span style={{ color: "#888", fontSize: "0.72rem" }}>{formatSize(file.size)}</span>
                </Typography>
                <Button size="small" variant="contained" href={file.url} target="_blank" sx={{ borderRadius: "8px", minWidth: 0, padding: "2px 10px" }}>
                  ⬇
                </Button>
              </Box>
            ))
          )}
        </Paper>
      )}
    </>
  );
};

export default FilesChip;
